import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Mail, Clock, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function AdminPendingMerchants() {
  const [items, setItems] = useState([]);
  const [resetting, setResetting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [stats, setStats] = useState(null);

  const refresh = () => api.get("/auth/admin/pending-merchants").then((r) => setItems(r.data || []));
  const loadStats = () => api.get("/admin/platform/stats").then((r) => setStats(r.data));

  useEffect(() => { refresh(); loadStats(); }, []);

  const approve = async (u) => {
    if (!window.confirm(`Aprovar comerciante ${u.name} (${u.email})?`)) return;
    try {
      await api.post(`/auth/admin/approve-merchant/${u.id}`);
      toast.success("Aprovado — email enviado.");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha"); }
  };

  const reject = async (u) => {
    if (!window.confirm(`Rejeitar (manter bloqueado) o comerciante ${u.email}?`)) return;
    try {
      await api.post(`/auth/admin/reject-merchant/${u.id}`);
      toast.success("Marcado como rejeitado.");
      refresh();
    } catch (e) { toast.error("Falha"); }
  };

  const doReset = async () => {
    if (confirmText !== "RESET-MORARBEM") return;
    setResetting(true);
    try {
      const r = await api.post(`/admin/platform/reset?confirm=RESET-MORARBEM`);
      toast.success("Plataforma zerada. Recarregando...");
      console.log("Reset stats:", r.data);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error("Falha ao resetar"); }
    finally { setResetting(false); setConfirmText(""); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-pending-merchants">
      <h1 className="font-display text-3xl font-bold text-blue-950 mb-1 flex items-center gap-2">
        <Mail size={28}/>Comerciantes aguardando aprovação
      </h1>
      <p className="text-stone-500 text-sm mb-6">Comerciantes com email confirmado precisam ser aprovados pelo administrador antes de cadastrar empresas e vender.</p>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Telefone</th>
                <th className="text-left px-4 py-3">CNPJ</th>
                <th className="text-left px-4 py-3">Email verificado</th>
                <th className="text-left px-4 py-3">Cadastrado em</th>
                <th className="text-right px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-stone-100" data-testid={`pending-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3 text-xs">{u.phone || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.cnpj || "—"}</td>
                  <td className="px-4 py-3">
                    {u.email_verified ?
                      <span className="text-emerald-700 text-xs font-bold uppercase flex items-center gap-1"><CheckCircle2 size={12}/>Sim</span> :
                      <span className="text-amber-700 text-xs font-bold uppercase flex items-center gap-1"><Clock size={12}/>Não</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-stone-500">{new Date(u.created_at).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" onClick={() => approve(u)} disabled={!u.email_verified} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg" data-testid={`approve-${u.id}`}>
                      <CheckCircle2 size={12} className="mr-1"/>Aprovar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => reject(u)} className="text-red-700 rounded-lg" data-testid={`reject-${u.id}`}>
                      <XCircle size={12} className="mr-1"/>Rejeitar
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-stone-400 text-sm">Nenhum comerciante pendente</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* DANGER ZONE - Reset Platform */}
      <Card className="mt-12 p-6 rounded-2xl border-2 border-red-300 bg-red-50">
        <div className="flex items-center gap-2 text-red-900 mb-2"><AlertTriangle size={20}/><h2 className="font-display text-xl font-bold">Zona de perigo — Resetar plataforma</h2></div>
        <p className="text-sm text-red-800 mb-3">Apaga TODAS as empresas, clientes, transações, vouchers, produtos, pedidos e payouts. Mantém: admin, planos, slides, blog, segmentos, categorias, cidades, configurações.</p>
        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3 text-xs" data-testid="reset-stats">
            {Object.entries(stats).filter(([k,v]) => v > 0).map(([k,v]) => (
              <div key={k} className="p-1.5 rounded bg-white border border-red-200">
                <div className="text-stone-500 text-[10px]">{k}</div>
                <div className="font-bold text-red-900">{v}</div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-red-900 mb-2">Digite <code className="bg-white px-1 rounded font-mono">RESET-MORARBEM</code> para confirmar:</p>
        <div className="flex gap-2 max-w-md">
          <input
            value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
            className="flex-1 rounded-xl border border-red-300 px-3 py-2 font-mono text-sm"
            placeholder="RESET-MORARBEM"
            data-testid="reset-confirm-input"
          />
          <Button onClick={doReset} disabled={confirmText !== "RESET-MORARBEM" || resetting} className="bg-red-700 hover:bg-red-800 text-white rounded-xl" data-testid="reset-platform-btn">
            <Trash2 size={14} className="mr-1"/>{resetting ? "Resetando..." : "Zerar plataforma"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
